import { TGlobalStores } from '@/editor/stores';
import { EDirection, EEntityType, ISelected } from '@/editor/types';
import { KEYBOARD_MOVE_COMMIT_DELAY, directionToOffset } from '@/editor/controller/helper/keyboardMove';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { debounce } from 'lodash-es';
import * as THREE from 'three';
import { modelManager } from '@/editor/manager/entityManager';
import { CommonController3d } from '../commonController';
import { rayEntity3d } from '../rayEntity';
import { handOverMouseDownToMultiSelect } from '../../utils';
import { OperateMultiModel } from './operateMultiModel';
import { OperateFurniture } from './operateFurniture';
import { OperateAssembly } from './operateAssembly';
import { OperateAbstract } from './operateAbstract';

interface IValidOperateMap {
  [EEntityType.Furniture]: typeof OperateFurniture;
  [EEntityType.Assembly]: typeof OperateAssembly;
  [EEntityType.MultiModel]: typeof OperateMultiModel;
}

type TOperateMap = IValidOperateMap & {
  [key in Exclude<EEntityType, keyof IValidOperateMap>]?: undefined;
};

const operateMap: TOperateMap = {
  [EEntityType.Furniture]: OperateFurniture,
  [EEntityType.Assembly]: OperateAssembly,
  [EEntityType.MultiModel]: OperateMultiModel
};

/** 3D 下可操作的实体都支持方向键微移（柱子在 3D 下不可选中，所以不在其中） */
function isKeyboardMoveType(type: EEntityType): type is keyof IValidOperateMap {
  return type === EEntityType.Furniture || type === EEntityType.Assembly || type === EEntityType.MultiModel;
}

enum EMouseButton {
  EMPTY = -1,
  LEFT = 0,
  MIDDLE = 1,
  RIGHT = 2
}

export class OperateEntityController3d extends CommonController3d {
  private _raycaster: THREE.Raycaster;
  private _mouse: THREE.Vector2;
  private _mouseDownX = 0;
  private _mouseDownY = 0;

  private _mouseDownEntityInfo: ISelected = { entityId: '', type: EEntityType.EMPTY };
  private _mouseDownButton = EMouseButton.EMPTY;
  private _currentOperate: OperateAbstract<EEntityType> | null = null;

  /** 方向键微移的操作实例，与鼠标拖拽的 _currentOperate 相互独立 */
  private _keyboardOperate: OperateAbstract<EEntityType> | null = null;
  private _keyboardMoveStartPoint: IPoint2d = { x: 0, y: 0 };
  private _commitKeyboardMove = debounce(() => this.finishKeyboardMove(), KEYBOARD_MOVE_COMMIT_DELAY);

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._raycaster = new THREE.Raycaster();
    this._mouse = new THREE.Vector2();
  }

  /**
   * 方向键微移选中实体
   *
   * 与拖拽一样走虚拟模型，连续按键只在最后提交一次，区别是微移不走吸附。柱子在 3D 下不可选中，所以不支持
   */
  public override moveEntityByDirection(direction: EDirection) {
    // 鼠标拖拽过程中不响应
    if (this._currentOperate) return;

    if (!this._keyboardOperate && !this.startKeyboardMove()) return;

    this._keyboardOperate?.moveByOffset(directionToOffset(direction));
    // 每次按键都把提交时机往后推
    this._commitKeyboardMove();
  }

  /** 目前需要结算的只有延时提交的方向键微移，没有进行中的微移时什么都不做 */
  public override flushSync() {
    this._commitKeyboardMove.flush();
  }

  private startKeyboardMove() {
    const { entityId, type } = this._gs.editorStateStore.selected;
    if (!entityId) return false;
    if (!isKeyboardMoveType(type)) return false;

    const OperateClass = operateMap[type];

    // 多选的数据不按 entityId 存放，统一用空 id 取
    const center =
      type === EEntityType.MultiModel
        ? this._gs.getDataByIdType('', EEntityType.MultiModel)?.center
        : this._gs.getDataByIdType(entityId, type)?.center;
    if (!center) return false;

    const operate: OperateAbstract<EEntityType> = new OperateClass(this._gs);
    operate.checkedEntity = modelManager.getEntity(entityId, type);

    // 多选里含门窗等不可移动的实体时会拒绝开始
    if (!operate.startMove(center)) {
      operate.dispose();
      return false;
    }

    this._keyboardOperate = operate;
    this._keyboardMoveStartPoint = center;

    // 修改全局移动操作状态标记
    this._gs.editorStateStore.moving = true;

    return true;
  }

  private finishKeyboardMove() {
    if (!this._keyboardOperate) return;

    // 所有支持微移的实体都用移动过程中记录的虚拟数据提交，传入的点不参与计算
    this._keyboardOperate.completeMove(this._keyboardMoveStartPoint);
    this._keyboardOperate.dispose();
    this._keyboardOperate = null;

    this._gs.editorStateStore.moving = false;
  }

  private startOperate(event: MouseEvent, selected: ISelected) {
    const clickedPoint = this.getPointOnPlane(event);
    if (!clickedPoint) return;

    const OperateClass = operateMap[selected.type];

    if (!OperateClass) return;

    this._currentOperate = new OperateClass(this._gs);
    this._currentOperate.checkedEntity = modelManager.getEntity(selected.entityId, selected.type);

    const started = this._currentOperate.startMove(clickedPoint);
    if (!started) {
      this._currentOperate.dispose();
      this._currentOperate = null;
      return;
    }

    this.enableRotate = false;
    this._gs.editorStateStore.moving = true;
  }

  protected override mouseDown(event: MouseEvent) {
    // 鼠标操作会改变选中状态，先结算掉延时提交的数据
    this.flushSync();

    // Ctrl/⌘（按平台）按住时左键直接进多选（点选加减）。
    // 它不能像 Shift 那样在 keydown 就切模式，否则 Ctrl+C / ⌘C 这类快捷键会因为菜单卸载而失效
    if (event.button === 0 && this._gs.keyboardStore.isMultiSelectModifierPressed()) {
      handOverMouseDownToMultiSelect(event);
      return;
    }

    this._mouseDownButton = event.button;
    const entityInfo = this.rayEntity(event);
    if (event.button === 0) {
      this._mouseDownX = event.clientX;
      this._mouseDownY = event.clientY;

      this._mouseDownEntityInfo = entityInfo;
    } else if (event.button === 2 || event.button === 1) {
      this._gs.editorStateStore.dragging = true;
    }

    const selected = this._gs.editorStateStore.selected;
    const entityId = selected.entityId;

    if (entityId !== entityInfo.entityId) return;

    this.startOperate(event, selected);
  }

  protected override mouseMove(event: MouseEvent) {
    switch (this._mouseDownButton) {
      case EMouseButton.LEFT:
        if (this.enableRotate) {
          this._gs.editorStateStore.rotating = true;
        } else {
          // TODO: 这里需要优化，不要用enableRotate来判断
          this._gs.editorStateStore.moving = true;

          const clickPoint = this.getPointOnPlane(event);
          if (!clickPoint) return;

          if (!this._currentOperate) return;
          this._currentOperate.move(clickPoint);
        }
        return;
      case EMouseButton.EMPTY: {
        const entityInfo = this.rayEntity(event);
        this._mouseDownEntityInfo = entityInfo;
        this._gs.editorStateStore.setHoverEntity(entityInfo);
        return;
      }
    }
  }

  protected override mouseUp(event: MouseEvent) {
    if (event.button === 0) {
      this._gs.editorStateStore.rotating = false;
      const entityInfo = this.rayEntity(event);

      if (this._currentOperate) {
        const clickPoint = this.getPointOnPlane(event);
        if (clickPoint) {
          this._currentOperate.completeMove(clickPoint);
        }
        this._currentOperate.dispose();
        this._currentOperate = null;
      }

      const samePoint = this._mouseDownX === event.clientX && this._mouseDownY === event.clientY;
      if (samePoint && entityInfo.entityId === this._mouseDownEntityInfo.entityId) {
        this._gs.editorStateStore.setSelected(entityInfo);
      }
    } else if (event.button === 2 || event.button === 1) {
      this._gs.editorStateStore.dragging = false;
    }

    this._gs.editorStateStore.moving = false;
    this.enableRotate = true;
    this._mouseDownButton = EMouseButton.EMPTY;
  }

  private rayEntity(event: MouseEvent) {
    return rayEntity3d(this._gs, event, this._raycaster, this._mouse);
  }

  override dispose() {
    // 控制器销毁前结算掉延时提交的数据，否则微移中的虚拟模型连带真实实体的隐藏状态一起丢失
    this.flushSync();
    this._gs.editorStateStore.moving = false;

    if (this._currentOperate) {
      this._currentOperate.dispose();
      this._currentOperate = null;
    }

    super.dispose();
  }
}
